import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { slide as BurgerMenu } from 'react-burger-menu';
import classNames from 'classnames';

import Logo from '../Logo';
import HeaderDropdown from '../HeaderDropdown';
import MenuBar from './MenuBar';
import MobileMenu from './MobileMenu';
import AccountMenu from './AccountMenu';
import LoginButton from './LoginButton';
import SignUpButton from './SignUpButton';
import AskListHeaderMenu from '../AskListHeaderMenu';
import ActivityTracker from '../../containers/TrackerContainer';
import QuestionCreateContainer from '../../containers/QuestionCreateContainer';
import Modal from '../Modal';

import LoginModal from '../LoginModal';
import SignUpModal from '../SignUpModal';
import ProfileEditorModal from '../ProfileEditorModal';
import ProfileSelectorModal from '../ProfileSelectorModal';
import ForgotPasswordModal from '../ForgotPasswordModal';
import InviteModal from '../InviteModal';
import BabyEditorModal from '../BabyEditorModal';
import BabyCreateModal from '../BabyCreateModal';
import LogoutWarningModal from '../Modals/LogoutWarningModal/LogoutWarningModal';
import RequestSentModal from '../RequestSentModal';
import { DiaryForgotPinModal } from '../diary';

import { Dashboard, Diary, Menu, User, Delete, Learn, Discharge } from '../icons';
import profileIcons from '../icons/profileIcons';
import playIcon from '../../assets/icons/dashboard-play.svg';

import './Header.scss';

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = (e) => setMatches(e.matches);
    
    if (media.addEventListener) {
      media.addEventListener('change', listener);
    } else {
      media.addListener(listener);
    }
    
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', listener);
      } else {
        media.removeListener(listener);
      }
    };
  }, [query]);

  return matches;
};

const Header = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  
  const {
    isAuthenticated, isAdmin, isStaff, profile, roles, history,
    menuShow, menuOpen, menuClose, menuItems, questions, fetchAskList,
    handleOpenLoginModal, handleCloseLoginModal, handleOpenSignUpModal, handleCloseSignUpModal, 
    handleLogout, onChangeProfileClick, handleSelectProfile 
  } = props;

  const [accountOpen, setAccountOpen] = useState(false);
  const [questionModalOpen, setQuestionModalOpen] = useState(false);

  const isMobile = useMediaQuery('(max-width: 767px)');

  const isQuestionsActive = useMemo(() => 
    location.pathname.endsWith('/questions'), 
  [location.pathname]);

  const closeQuestionModal = () => setQuestionModalOpen(false);

  const accountMenuContent = useMemo(() => (
    <AccountMenu
      isAdmin={isAdmin}
      isStaff={isStaff}
      profile={profile}
      closeBurgerMenu={() => setAccountOpen(false)}
      handleOpenProfileEditorModal={props.handleOpenProfileEditorModal}
      onChangeProfileClick={onChangeProfileClick}
      handleLogout={handleLogout}
      t={t}
    />
  ), [isAdmin, isStaff, profile, handleLogout, onChangeProfileClick, t, props.handleOpenProfileEditorModal]);

  return (
    <header className={classNames('header', {
      'user-is-authenticated': isAuthenticated,
      'user-not-authenticated': !isAuthenticated
    })}>
      <div className="header__primary">
        <div className="header-button header-button--menu">
          <BurgerMenu
            isOpen={menuShow}
            width="87.5%"
            menuClassName="burger-menu burger-menu--left"
            overlayClassName="burger-menu-overlay"
            className={classNames({
              'burger-menu--open': menuShow
            })}
            customCrossIcon={<Delete width="32" height="32" viewBox="0 0 32 32"/>}
            customBurgerIcon={<Menu width="32" height="32" viewBox="0 0 32 32" label={t('nav.menu')} />}
            onStateChange={state => (state.isOpen ? menuOpen?.() : menuClose?.())}
          >
            <div className="header__menu-header">
              <NavLink end to="/" onClick={() => menuClose?.()}>
                <Logo type="vertical" className="header__logo-image" />
              </NavLink>
            </div>
            {menuItems && (
              <MobileMenu
                {...props}
                menuClose={() => menuClose?.()}
                openModal={() => setQuestionModalOpen(true)}
              />
            )}
          </BurgerMenu>
        </div>

        <div className="header__logo">
          <NavLink end to="/">
            <div className="header__logo--desktop">
              <Logo className="header__logo-image" />
            </div>
          </NavLink>
        </div>

        {!isAuthenticated && (
          <div className="header__logo--mobile">
            <NavLink end to="/">
              <Logo type="mobile" className="header__logo-image" />
            </NavLink>
          </div>
        )}

        {isAuthenticated && (
          <>
            <div className="header-button header-button--discharge">
              <NavLink className="header-button__link" to="/discharge">
                <Discharge label={t('nav.dischargeTitle')} width="32" height="32" viewBox="0 0 32 32"/>
              </NavLink>
            </div>

            <div className="header-button header-button--learn">
              <NavLink className="header-button__link" to="/learn">
                <Learn label={t('nav.learn')} width="32" height="32" viewBox="0 0 32 32"/>
              </NavLink>
            </div>

            {isMobile ? (
              <>
                <div className="header-button header-button--tracker"><ActivityTracker /></div>
                <div className="header-button header-button--videos">
                  <NavLink className="header-button__link" to="/videos">
                    <div className="icon-with-label">
                      <img src={playIcon} alt="" className="play-icon icon-with-label__icon" />
                      <div className="icon-with-label__label">{t('nav.videos')}</div>
                    </div>
                  </NavLink>
                </div>
                <div className="header-button header-button--dashboard">
                  <NavLink className="header-button__link" to="/dashboard">
                    <Dashboard height="32" width="32" label={t('nav.dashboard')} viewBox="0 0 32 32"/>
                  </NavLink>
                </div>
              </>
            ) : (
              <>
                <div className="header-button header-button--videos">
                  <NavLink className="header-button__link" to="/videos">
                    <div className="icon-with-label">
                      <img src={playIcon} alt="" className="play-icon icon-with-label__icon" />
                      <div className="icon-with-label__label">{t('nav.videos')}</div>
                    </div>
                  </NavLink>
                </div>
                <div className="header-button header-button--tracker"><ActivityTracker /></div>
                
                <div className="header-button header-button--dashboard">
                  <NavLink className="header-button__link" to="/dashboard">
                    <Dashboard height="32" width="32" label={t('nav.dashboard')} viewBox="0 0 32 32"/>
                  </NavLink>
                </div>

                <div className="header-button header-button--diary">
                  <NavLink className="header-button__link" to="/diary">
                    <Diary label={t('nav.diary')} width="32" height="32" viewBox="0 0 32 32"/>
                  </NavLink>
                </div>
              </>
            )}

            <div className={classNames('header-button header-button--ask-list', { 'active': isQuestionsActive })}>
              <AskListHeaderMenu profile={profile} questions={questions} fetchAskList={fetchAskList} />
            </div>
          </>
        )}

        <div className="header-button header-button--account">
          {isAuthenticated ? (
            <div>
              <HeaderDropdown
                className="header-button--account-dropdown"
                displayArrow={false}
                containerWidth="280px"
                title={
                  <img
                    src={profileIcons[profile?.icon]}
                    width="40"
                    height="40"
                    className="header-button__account-greeting-icon"
                    alt=""
                  />
                }
                align="right"
              >
                {accountMenuContent}
              </HeaderDropdown>
              <BurgerMenu
                tabIndex="-1"
                isOpen={accountOpen}
                burgerButtonClassName="header-button--account-burger"
                right
                width="87.5%"
                menuClassName="burger-menu burger-menu--right"
                overlayClassName="burger-menu-overlay"
                customCrossIcon={<Delete label="close" />}
                customBurgerIcon={<User label={t('nav.account')} />}
                onStateChange={state => setAccountOpen(state.isOpen)}
              >
                <div className="header__menu-header">
                  <Logo className="header__menu-logo" type="icon" />
                  <h4>{t('nav.account')}</h4>
                </div>
                {accountMenuContent}
              </BurgerMenu>
            </div>
          ) : (
            <div className="header-buttons">
              <SignUpButton location={location} handleOpenSignUpModal={handleOpenSignUpModal} />
              <LoginButton location={location} handleOpenLoginModal={handleOpenLoginModal} />
            </div>
          )}
        </div>
      </div>

      {menuItems && (
        <div className="header__secondary">
          <MenuBar location={location} isAuthenticated={isAuthenticated} menuItems={menuItems} />
        </div>
      )}

      <SignUpModal
        {...(props.signUpProps || {})}
        isOpen={props.isSignUpModalOpen}
        onRequestClose={handleCloseSignUpModal}
        closeSignUpModal={handleCloseSignUpModal}
        handleOpenLoginModal={handleOpenLoginModal}
        onOpenRequestSent={props.handleOpenRequestSentModal}
      />
      <LoginModal
        {...(props.loginProps || {})}
        isOpen={props.isLoginModalOpen}
        onRequestClose={handleCloseLoginModal}
        closeLoginModal={handleCloseLoginModal}
        handleOpenSignUpModal={handleOpenSignUpModal}
        history={history}
        profiles={props.profiles || []}
        isLoadingProfiles={props.isLoadingProfiles}
        isWelcomeMessageVisible={!isAuthenticated}
        selectedProfileId={props.profile?.id}
        onSelectProfile={handleSelectProfile}
        onEditProfile={props.handleOpenProfileEditorModal}
        onOpenForgotPassword={props.handleOpenForgotPasswordModal}
        isManagingProfiles={props.isManagingProfiles}
        onEnterManageMode={props.handleEnterProfileManageMode}
        onLeaveManageMode={props.handleLeaveProfileManageMode}
        onChangeProfileClick={onChangeProfileClick}
      />
      <ProfileSelectorModal
        profiles={props.profiles || []}
        isLoadingProfiles={props.isLoadingProfiles}
        isOpen={props.isProfileSelectorModalOpen}
        isManagingProfiles={props.isManagingProfiles}
        isWelcomeMessageVisible={!isAuthenticated}
        selectedProfileId={props.profile?.id}
        onRequestClose={props.handleCloseProfileSelectorModal}
        onSelectProfile={handleSelectProfile}
        onEditProfile={props.handleOpenProfileEditorModal}
        onEnterManageMode={props.handleEnterProfileManageMode}
        onLeaveManageMode={props.handleLeaveProfileManageMode}
      />
      <ProfileEditorModal
        isOpen={props.isProfileEditorModalOpen}
        isEditingCurrentProfile={props.isEditingCurrentProfile}
        initialValues={props.editingProfile}
        onRequestClose={props.handleCloseProfileEditorModal}
        onDelete={props.handleDeleteProfile}
        roles={roles}
        t={t}
      />
      <BabyEditorModal
        isOpen={!!props.editingBaby}
        initialValues={props.editingBaby}
        onRequestClose={props.handleCloseBabyEditorModal}
        onBabySaved={props.handleCloseBabyEditorModal}
      />
      <BabyCreateModal
        isOpen={props.isBabyCreateModalOpen}
        onRequestClose={props.handleCloseBabyCreateModal}
        onBabyCreated={props.handleCloseBabyCreateModal}
      />
      <ForgotPasswordModal
        {...(props.forgotPasswordProps || {})}
        isOpen={props.isForgotPasswordModalOpen}
        onRequestClose={props.handleCloseForgotPasswordModal}
        onSubmit={props.handleForgotPassword}
        t={t}
      />
      <InviteModal
        {...(props.inviteProps || {})}
        isOpen={props.isInviteModalOpen}
        onRequestClose={props.handleCloseInviteModal}
        onSubmit={props.handleInvite}
      />
      
      {/* Diary Modal updated with all required status props */}
      <DiaryForgotPinModal
        isOpen={props.isForgotPinModalOpen}
        onRequestClose={props.handleCloseForgotPinModal}
        onSubmit={props.handleForgotPin}
        isLoading={props.isForgotPinLoading}
        isSuccessMessageVisible={props.isForgotPinSuccess}
        isFailureMessageVisible={props.isForgotPinFailure}
      />

      <RequestSentModal
        isOpen={props.isRequestSentModalOpen}
        onRequestClose={props.handleCloseRequestSentModal}
      />
      {profile && (
        <Modal isOpen={questionModalOpen} onRequestClose={closeQuestionModal}>
          <QuestionCreateContainer onQuestionAdded={closeQuestionModal} />
        </Modal>
      )}
      <LogoutWarningModal history={props.history} />
    </header>
  );
};

Header.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  profile: PropTypes.object,
  handleLogout: PropTypes.func.isRequired,
};

export default Header;
